import React, { useState } from 'react';
import { Trash2, Upload, Loader2, Image as ImageIcon, Plus, X, CreditCard, Building2, User, Hash, Package, MapPin, Phone, Mail, Send, CheckCircle2, MessageSquare, Info, AlertCircle } from 'lucide-react';
import { getUploadUrlWithQuota } from '../../services/uploadWithQuota';
import { db } from '../../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import ColorSwatchPicker from './ColorSwatchPicker';

interface PackageOption {
  id: string;
  name: string;
  price: string;
}

interface PaymentBlockData {
  qrCodeUrl?: string;
  accountName: string;
  bankName: string;
  accountNumber: string;
  packages: PackageOption[];
  successMessage: string;
  lineUrl?: string;
  phoneNumber?: string;
  email?: string;
  accentColor?: string;
}

const DEFAULT_ACCENT_COLOR = '#f97316';

function hexToRgba(hex: string, alpha: number): string {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return `rgba(249,115,22,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

interface ComparisonColumn {
  id: string;
  title: string;
  price: string;
  features: string[];
  highlighted: boolean;
  buttonText: string;
  buttonUrl: string;
  imageUrl?: string;
}

interface ComparisonTableData {
  columns: ComparisonColumn[];
}

interface PaymentBlockProps {
  data: PaymentBlockData;
  onChange: (data: PaymentBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
  salePageId?: string;
  salePageSlug?: string;
  comparisonTableData?: ComparisonTableData;
}

const PaymentBlock: React.FC<PaymentBlockProps> = ({ data, onChange, onDelete, isPreview, salePageId, salePageSlug, comparisonTableData }) => {
  const [uploadingQR, setUploadingQR] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Customer form state
  const [selectedPackage, setSelectedPackage] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [slipProgress, setSlipProgress] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // รวมแพ็คเก็จจากตารางเปรียบเทียบกับแพ็คเก็จที่มีอยู่
  const getPackages = (): PackageOption[] => {
    const existingPackages = data?.packages || [];
    
    // ถ้ามีตารางเปรียบเทียบ ให้ดึงข้อมูลมา
    if (comparisonTableData?.columns && comparisonTableData.columns.length > 0) {
      const comparisonPackages: PackageOption[] = comparisonTableData.columns.map(col => ({
        id: `comparison-${col.id}`,
        name: col.title,
        price: col.price
      }));
      
      // รวมแพ็คเก็จจากตารางเปรียบเทียบกับแพ็คเก็จที่มีอยู่ (ไม่ซ้ำกัน)
      const existingIds = new Set(existingPackages.map(p => p.id));
      const newPackages = comparisonPackages.filter(p => !existingIds.has(p.id));
      
      return [...comparisonPackages, ...existingPackages.filter(p => !p.id.startsWith('comparison-'))];
    }
    
    return existingPackages;
  };

  const safeData: PaymentBlockData = {
    qrCodeUrl: data?.qrCodeUrl || '',
    accountName: data?.accountName || '',
    bankName: data?.bankName || '',
    accountNumber: data?.accountNumber || '',
    packages: getPackages(),
    successMessage: data?.successMessage || 'ขอบคุณที่สั่งซื้อ! เราจะติดต่อกลับเร็วๆ นี้',
    lineUrl: data?.lineUrl || '',
    phoneNumber: data?.phoneNumber || '',
    email: data?.email || '',
    accentColor: data?.accentColor || DEFAULT_ACCENT_COLOR
  };
  const accentColor = safeData.accentColor || DEFAULT_ACCENT_COLOR;

  const handleQRUpload = async (file: File) => {
    try {
      setUploadingQR(true);
      setUploadProgress(0);
      
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (prog, msg) => {
          setUploadProgress(prog);
          console.log(msg);
        }
      );
      
      onChange({ ...safeData, qrCodeUrl: result.publicUrl });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('อัปโหลด QR Code ล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploadingQR(false);
      setUploadProgress(0);
    }
  };

  const addPackage = () => {
    const newPackage: PackageOption = {
      id: Date.now().toString(),
      name: 'แพ็คเก็จใหม่',
      price: '0'
    };
    onChange({ ...safeData, packages: [...safeData.packages, newPackage] });
  };

  const updatePackage = (id: string, field: keyof PackageOption, value: string) => {
    const updatedPackages = safeData.packages.map(pkg =>
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    );
    onChange({ ...safeData, packages: updatedPackages });
  };

  const deletePackage = (id: string) => {
    onChange({ ...safeData, packages: safeData.packages.filter(pkg => pkg.id !== id) });
  };

  const handleSlipUpload = async (file: File) => {
    try {
      setUploadingSlip(true);
      setSlipProgress(0);
      
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (prog, msg) => {
          setSlipProgress(prog);
          console.log(msg);
        }
      );
      
      setSlipUrl(result.publicUrl);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('อัปโหลดสลิปล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploadingSlip(false);
      setSlipProgress(0);
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedPackage || !slipUrl || !customerName || !customerPhone) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const selectedPkg = safeData.packages.find(pkg => pkg.id === selectedPackage);
      
      await addDoc(collection(db, 'paymentNotifications'), {
        salePageSlug: salePageSlug || 'unknown',
        packageId: selectedPackage,
        packageName: selectedPkg?.name || '',
        packagePrice: selectedPkg?.price || '',
        slipUrl,
        customerName,
        customerAddress,
        customerPhone,
        customerEmail,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      setShowSuccessModal(true);
      
      // Reset form
      setSelectedPackage('');
      setSlipUrl('');
      setCustomerName('');
      setCustomerAddress('');
      setCustomerPhone('');
      setCustomerEmail('');
    } catch (error) {
      console.error('Submit failed:', error);
      alert('ส่งข้อมูลล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isPreview) {
    return (
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-orange-200">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: accentColor }}>
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">ชำระเงิน</h3>
            <p className="text-gray-600">กรุณาโอนเงินและแจ้งโอนเพื่อยืนยันคำสั่งซื้อ</p>
          </div>

          {/* QR Code */}
          {safeData.qrCodeUrl && (
            <div className="bg-white rounded-xl p-6 mb-6 shadow-lg">
              <h4 className="font-bold text-gray-900 mb-4 text-center">สแกน QR Code เพื่อชำระเงิน</h4>
              <img
                src={safeData.qrCodeUrl}
                alt="QR Code"
                className="w-64 h-64 mx-auto object-contain"
              />
            </div>
          )}

          {/* Bank Info */}
          <div className="bg-white rounded-xl p-6 mb-6 shadow-lg">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: accentColor }} />
              ข้อมูลบัญชี
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">ชื่อบัญชี:</span>
                <span className="font-semibold text-gray-900">{safeData.accountName || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">ธนาคาร:</span>
                <span className="font-semibold text-gray-900">{safeData.bankName || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">เลขบัญชี:</span>
                <span className="font-semibold text-gray-900 font-mono">{safeData.accountNumber || '-'}</span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Send className="w-5 h-5" style={{ color: accentColor }} />
              แจ้งโอนเงิน
            </h4>

            <div className="space-y-4">
              {/* Package Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" style={{ color: accentColor }} />
                  เลือกแพ็คเก็จ *
                </label>
                <select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                  required
                >
                  <option value="">-- เลือกแพ็คเก็จ --</option>
                  {safeData.packages.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name} - {pkg.price} ฿
                    </option>
                  ))}
                </select>
              </div>

              {/* Slip Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" style={{ color: accentColor }} />
                  อัปโหลดสลิปโอนเงิน *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleSlipUpload(file);
                      e.target.value = '';
                    }
                  }}
                  disabled={uploadingSlip}
                  className="hidden"
                  id="slip-upload"
                />
                {slipUrl ? (
                  <div className="relative group">
                    <img
                      src={slipUrl}
                      alt="สลิปโอนเงิน"
                      className="w-full h-48 object-contain rounded-lg border-2 border-green-200 bg-green-50"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all rounded-lg flex items-center justify-center gap-3">
                      <label
                        htmlFor="slip-upload"
                        className="cursor-pointer bg-white text-orange-600 px-4 py-2 rounded-lg hover:bg-orange-50 flex items-center gap-2 font-semibold"
                      >
                        <Upload className="w-4 h-4" />
                        เปลี่ยนรูป
                      </label>
                      <button
                        onClick={() => setSlipUrl('')}
                        className="bg-white text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-2 font-semibold"
                      >
                        <Trash2 className="w-4 h-4" />
                        ลบรูป
                      </button>
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="slip-upload"
                    className={`flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                      uploadingSlip
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'
                    }`}
                  >
                    {uploadingSlip ? (
                      <div className="text-center">
                        <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-3" />
                        <p className="text-lg font-bold text-orange-600">{slipProgress}%</p>
                        <p className="text-sm text-gray-600">กำลังอัปโหลด...</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm font-semibold text-gray-700 mb-1">อัปโหลดสลิปโอนเงิน</p>
                        <p className="text-xs text-gray-500">PNG, JPG</p>
                      </div>
                    )}
                  </label>
                )}
              </div>

              {/* Customer Info */}
              <div className="border-t pt-4">
                <h5 className="font-bold text-gray-900 mb-4">ข้อมูลสำหรับจัดส่ง</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" style={{ color: accentColor }} />
                      ชื่อ-นามสกุล *
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                      placeholder="นาย/นาง ..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" style={{ color: accentColor }} />
                      ที่อยู่
                    </label>
                    <textarea
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                      rows={3}
                      placeholder="ที่อยู่สำหรับจัดส่ง"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                        <Phone className="w-4 h-4" style={{ color: accentColor }} />
                        เบอร์โทร *
                      </label>
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                        placeholder="0xx-xxx-xxxx"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                        <Mail className="w-4 h-4" style={{ color: accentColor }} />
                        Email
                      </label>
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitPayment}
                disabled={isSubmitting || !selectedPackage || !slipUrl || !customerName || !customerPhone}
                className="w-full py-4 text-white rounded-lg font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: accentColor }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    กำลังส่งข้อมูล...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    ยืนยันการแจ้งโอน
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSuccessModal(false)}>
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full mx-4 border border-green-100 relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-green-50 to-emerald-50 rounded-full blur-3xl opacity-40 pointer-events-none"></div>
              
              <button
                onClick={() => setShowSuccessModal(false)}
                className="absolute top-4 right-4 z-20 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative z-10">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-12 h-12 text-white" />
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 mb-3">ส่งข้อมูลสำเร็จ!</h3>
                  
                  <div className="bg-green-50 rounded-xl p-6 mb-6">
                    <p className="text-gray-700 leading-relaxed">
                      {safeData.successMessage}
                    </p>
                  </div>

                  {/* LINE Contact - Priority */}
                  {safeData.lineUrl && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200 mb-4">
                      <div className="flex items-start gap-3 mb-4">
                        <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-700 text-left leading-relaxed">
                          <strong className="text-green-700">เพื่อความรวดเร็วในการตรวจสอบสลิปแจ้งโอนเงิน</strong><br />
                          กรุณาคลิกที่ลิงค์เพื่อแจ้งทาง LINE ได้เลยค่ะ
                        </p>
                      </div>
                      <a
                        href={safeData.lineUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                      >
                        <MessageSquare className="w-5 h-5" />
                        แจ้งทาง LINE ทันที
                      </a>
                    </div>
                  )}

                  {/* Other Contact Info */}
                  {(safeData.phoneNumber || safeData.email) && (
                    <div className="rounded-xl p-6 border-2" style={{ background: `linear-gradient(to bottom right, ${hexToRgba(accentColor, 0.08)}, ${hexToRgba(accentColor, 0.12)})`, borderColor: hexToRgba(accentColor, 0.3) }}>
                      <h4 className="font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
                        <Phone className="w-5 h-5" style={{ color: accentColor }} />
                        ช่องทางติดต่ออื่นๆ
                      </h4>
                      <div className="space-y-3">
                        {safeData.phoneNumber && (
                          <a
                            href={`tel:${safeData.phoneNumber}`}
                            className="block text-center text-gray-700 transition-all"
                          >
                            <Phone className="w-4 h-4 inline mr-2" />
                            {safeData.phoneNumber}
                          </a>
                        )}
                        {safeData.email && (
                          <a
                            href={`mailto:${safeData.email}`}
                            className="block text-center text-gray-700 transition-all"
                          >
                            <Mail className="w-4 h-4 inline mr-2" />
                            {safeData.email}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setShowSuccessModal(false)}
                    className="mt-6 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all"
                  >
                    ปิด
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-orange-500" />
            ระบบชำระเงิน
          </h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">รับชำระเงินและแจ้งโอน</p>
              <p className="text-gray-300">สร้างระบบชำระเงินด้วย QR Code หรือโอนธนาคาร ให้ลูกค้าอัปโหลดสลิปและกรอกข้อมูล คำสั่งจะแจ้งเตือนใน Dashboard</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Accent Color */}
        <div className="flex items-center gap-2">
          <ColorSwatchPicker
            compact
            color={accentColor}
            fallback={DEFAULT_ACCENT_COLOR}
            onChange={(hex) => onChange({ ...safeData, accentColor: hex })}
          />
          <span className="text-sm font-bold text-gray-700">สีธีมหลัก (ไอคอน/ปุ่มยืนยัน)</span>
        </div>

        {/* QR Code Upload */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-orange-500" />
            QR Code ชำระเงิน
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleQRUpload(file);
                e.target.value = '';
              }
            }}
            disabled={uploadingQR}
            className="hidden"
            id="qr-upload"
          />
          {safeData.qrCodeUrl ? (
            <div className="relative group">
              <img
                src={safeData.qrCodeUrl}
                alt="QR Code"
                className="w-48 h-48 object-contain rounded-xl border-2 border-gray-200"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all rounded-xl flex items-center justify-center gap-3">
                <label
                  htmlFor="qr-upload"
                  className="cursor-pointer bg-white text-orange-600 px-4 py-2 rounded-lg hover:bg-orange-50 flex items-center gap-2 font-semibold"
                >
                  <Upload className="w-4 h-4" />
                  เปลี่ยนรูป
                </label>
                <button
                  onClick={() => onChange({ ...safeData, qrCodeUrl: '' })}
                  className="bg-white text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-2 font-semibold"
                >
                  <Trash2 className="w-4 h-4" />
                  ลบรูป
                </button>
              </div>
            </div>
          ) : (
            <label
              htmlFor="qr-upload"
              className={`flex flex-col items-center justify-center h-48 w-48 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                uploadingQR
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'
              }`}
            >
              {uploadingQR ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-3" />
                  <p className="text-lg font-bold text-orange-600">{uploadProgress}%</p>
                  <p className="text-sm text-gray-600">กำลังอัปโหลด...</p>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-gray-700 mb-1">อัปโหลด QR Code</p>
                  <p className="text-xs text-gray-500">PNG, JPG</p>
                </div>
              )}
            </label>
          )}
        </div>

        {/* Bank Account Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-orange-500" />
              ชื่อบัญชี
            </label>
            <input
              type="text"
              value={safeData.accountName}
              onChange={(e) => onChange({ ...safeData, accountName: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
              placeholder="นาย/นาง ..."
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-orange-500" />
              ธนาคาร
            </label>
            <input
              type="text"
              value={safeData.bankName}
              onChange={(e) => onChange({ ...safeData, bankName: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
              placeholder="ธนาคาร..."
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
            <Hash className="w-4 h-4 text-orange-500" />
            เลขบัญชี
          </label>
          <input
            type="text"
            value={safeData.accountNumber}
            onChange={(e) => onChange({ ...safeData, accountNumber: e.target.value })}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all font-mono"
            placeholder="xxx-x-xxxxx-x"
          />
        </div>

        {/* Package Options */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-3">รายการแพ็คเก็จ</label>
          
          {/* แจ้งเตือนว่ามีการดึงข้อมูลจากตารางเปรียบเทียบ */}
          {comparisonTableData?.columns && comparisonTableData.columns.length > 0 && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">ดึงข้อมูลจากตารางเปรียบเทียบแล้ว</p>
                <p className="text-blue-700">ระบบดึงแพ็คเก็จ {comparisonTableData.columns.length} รายการจากตารางเปรียบเทียบอัตโนมัติ คุณสามารถเพิ่มแพ็คเก็จเองได้ด้านล่าง</p>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            {safeData.packages.map((pkg) => (
              <div key={pkg.id} className="flex gap-2">
                <input
                  type="text"
                  value={pkg.name}
                  onChange={(e) => updatePackage(pkg.id, 'name', e.target.value)}
                  className={`flex-1 px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all ${
                    pkg.id.startsWith('comparison-') 
                      ? 'border-blue-200 bg-blue-50/50' 
                      : 'border-gray-200'
                  }`}
                  placeholder="ชื่อแพ็คเก็จ"
                  disabled={pkg.id.startsWith('comparison-')}
                />
                <input
                  type="text"
                  value={pkg.price}
                  onChange={(e) => updatePackage(pkg.id, 'price', e.target.value)}
                  className={`w-32 px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all ${
                    pkg.id.startsWith('comparison-') 
                      ? 'border-blue-200 bg-blue-50/50' 
                      : 'border-gray-200'
                  }`}
                  placeholder="ราคา"
                  disabled={pkg.id.startsWith('comparison-')}
                />
                <button
                  onClick={() => deletePackage(pkg.id)}
                  className="flex-shrink-0 text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={pkg.id.startsWith('comparison-')}
                  title={pkg.id.startsWith('comparison-') ? 'แพ็คเก็จจากตารางเปรียบเทียบ ลบไม่ได้' : 'ลบแพ็คเก็จ'}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
            <button
              onClick={addPackage}
              className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm font-semibold text-gray-600 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              เพิ่มแพ็คเก็จเอง
            </button>
          </div>
        </div>

        {/* Success Message & Contact Info */}
        <div className="border-t pt-6">
          <h4 className="font-bold text-gray-900 mb-4">ข้อความหลังชำระเงิน</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">ข้อความขอบคุณ</label>
              <textarea
                value={safeData.successMessage}
                onChange={(e) => onChange({ ...safeData, successMessage: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                rows={3}
                placeholder="ขอบคุณที่สั่งซื้อ! เราจะติดต่อกลับเร็วๆ นี้"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">LINE URL</label>
                <input
                  type="text"
                  value={safeData.lineUrl}
                  onChange={(e) => onChange({ ...safeData, lineUrl: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                  placeholder="https://line.me/ti/p/..."
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">เบอร์โทร</label>
                <input
                  type="text"
                  value={safeData.phoneNumber}
                  onChange={(e) => onChange({ ...safeData, phoneNumber: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                  placeholder="0xx-xxx-xxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={safeData.email}
                  onChange={(e) => onChange({ ...safeData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                  placeholder="email@example.com"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentBlock;
